import { useState, useContext, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import UserContext from "../UserContext";
import ThemeContext from "../ThemeContext";
import { useLocation, Link } from "react-router-dom";
import auth from "../Account/Auth";
import Logo from "../Components/Brand/Logo";
import Logotype from "../Components/Brand/Logotype";

function Sidebar({ isSidebarExpanded, setIsSidebarExpanded }) {
  const { t } = useTranslation(["sidebar"]);
  const [user] = useContext(UserContext);
  const [theme, setTheme] = useContext(ThemeContext);
  const [copied, setCopied] = useState(false);
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const location = useLocation();

  const logoColor = "#f04c23";

  // Render the sidebar if a session object exists in localStorage,
  // regardless of access token validity. We'll refresh in the background.
  const hasSession = auth.sessionExist();
  const refreshingRef = useRef(false);
  const lastRefreshAtRef = useRef(0);
  const timerIdRef = useRef(null);

  // Background refresh: on mount + schedule before expiry
  useEffect(() => {
    const DEBUG_SIDEBAR = true;
    const log = (...args) =>
      DEBUG_SIDEBAR && console.info("[sidebar][session]", ...args);

    // tunables
    const REFRESH_BUFFER_MS = 60 * 1000; // refresh 1 minute before exp
    const MIN_DELAY_MS = 30 * 1000; // never schedule < 30s
    const MIN_REFRESH_COOLDOWN_MS = 30 * 1000; // avoid refiring within 30s
    const MAX_JITTER_MS = 5 * 1000; // up to 5s random jitter

    const isHardAuthFailure = (e) => {
      const msg = String(e?.message || e || "");
      const code = String(e?.code || e?.name || "");
      return /NotAuthorizedException|InvalidRefreshToken|No current user|User is not authenticated/i.test(
        msg + " " + code
      );
    };

    const clearTimer = () => {
      if (timerIdRef.current) {
        clearTimeout(timerIdRef.current);
        log("cleanup: cleared timer", timerIdRef.current);
        timerIdRef.current = null;
      }
    };

    if (!hasSession) {
      log("no session in localStorage → nothing to schedule");
      return () => clearTimer();
    }

    const nextDelay = () => {
      try {
        const token = auth.getAccessToken?.();
        if (!token) {
          log("nextDelay: no access token → using MIN_DELAY_MS");
          return MIN_DELAY_MS;
        }
        const expMs = auth.getExp(token); // epoch ms
        const nowMs = Date.now();
        const msUntilExp = expMs - nowMs;
        const msUntilRefresh = msUntilExp - REFRESH_BUFFER_MS;

        log(
          "nextDelay:",
          "now=",
          new Date(nowMs).toISOString(),
          "exp=",
          new Date(expMs).toISOString(),
          "msUntilExp=",
          msUntilExp,
          "msUntilRefresh=",
          msUntilRefresh
        );

        if (msUntilRefresh <= 0) return 0; // inside buffer → refresh now
        const baseDelay = Math.max(msUntilRefresh, MIN_DELAY_MS);
        const jitter = Math.floor(Math.random() * MAX_JITTER_MS);
        return baseDelay + jitter;
      } catch (e) {
        log("nextDelay error:", e?.message || e);
        return MIN_DELAY_MS;
      }
    };

    const scheduleNext = (context) => {
      clearTimer();
      const delay = nextDelay();
      if (delay === 0) {
        log(`scheduleNext(${context}): within buffer → triggering refresh now`);
        // schedule on next tick to avoid deep sync recursion
        timerIdRef.current = window.setTimeout(() => {
          void triggerRefresh("buffer_reached");
        }, 0);
        return;
      }
      log(
        `scheduleNext(${context}): scheduling in ~${Math.round(delay / 1000)}s`
      );
      timerIdRef.current = window.setTimeout(
        () => void triggerRefresh("timer"),
        delay
      );
    };

    const triggerRefresh = async (reason) => {
      const now = Date.now();

      if (refreshingRef.current) {
        log(`refresh skipped (reason=${reason}) → already in-flight`);
        scheduleNext("in_flight");
        return;
      }
      if (now - lastRefreshAtRef.current < MIN_REFRESH_COOLDOWN_MS) {
        const remaining =
          MIN_REFRESH_COOLDOWN_MS - (now - lastRefreshAtRef.current);
        log(
          `refresh skipped (reason=${reason}) → cooldown ${Math.ceil(
            remaining / 1000
          )}s`
        );
        scheduleNext("cooldown");
        return;
      }

      refreshingRef.current = true;
      log(`refresh start (reason=${reason})`);
      try {
        await auth.refresh(); // updates localStorage tokens/profile
        const newToken = auth.getAccessToken?.();
        if (newToken) {
          const newExp = auth.getExp(newToken);
          log("refresh success → new exp:", new Date(newExp).toISOString());
        } else {
          log("refresh success but no new token found");
        }
      } catch (err) {
        if (isHardAuthFailure(err)) {
          log("hard auth failure → clearing session & redirecting to /login");
          clearTimer();
          auth.logout(); // clears storage & navigates
          return; // stop scheduling entirely
        }
        log("refresh error (transient):", err?.message || err);
        // fall through: reschedule after cooldown
      } finally {
        refreshingRef.current = false;
        lastRefreshAtRef.current = Date.now();
        scheduleNext("post_refresh");
      }
    };

    // kick off
    scheduleNext("initial");

    return () => clearTimer();
  }, [hasSession]);

  // Pull user info from localStorage as before
  const userFullName = auth.getProperty({
    storageKey: "userSession",
    path: ["profileObj", "name"],
    fallback: "",
  });
  const userPlan = auth.getProperty({
    storageKey: "userSession",
    path: ["profileObj", "planStatus", "status"],
    fallback: "unknown",
  });
  const userPic = auth.getProperty({
    storageKey: "userSession",
    path: ["profileObj", "pic"],
    fallback: "/app-assets/img/standard-user-pic.png",
  });
  const userAgency = auth.getProperty({
    storageKey: "userSession",
    path: ["profileObj", "agencyId"],
    fallback: false,
  });
  const userSlug = auth.getProperty({
    storageKey: "userSession",
    path: ["profileObj", "slugName"],
    fallback: "None",
  });
  const username = auth.getProperty({
    storageKey: "userSession",
    path: ["profileObj", "username"],
    fallback: "",
  });

  let shadowColorClass =
    userPlan === "professional"
      ? "tw-shadow-[0px_0px_12px_-2px_#d6340e]"
      : "tw-shadow-[0px_0px_12px_-2px_#a0aec0]";

  const publicAddress = `/${userSlug !== "None" ? userSlug : username}`;

  const discountData = auth.getProperty({
    storageKey: "userDiscount",
    path: [],
    fallback: {},
  });
  let discountPercentage = discountData?.discountAmount;

  function setDomTheme(theme) {
    const root = window.document.getElementById("root");
    const html = window.document.getElementById("html");
    root?.setAttribute("data-theme", theme);
    html?.setAttribute("data-theme", theme);
    window.localStorage.setItem("theme", theme);
  }

  const handleCopy = async () => {
    try {
      const protocol = window.location.protocol;
      const host = window.location.host;
      const userSlug = auth.getProperty({
        storageKey: "userSession",
        path: ["profileObj", "slugName"],
        fallback: "None",
      });
      const username = auth.getProperty({
        storageKey: "userSession",
        path: ["profileObj", "username"],
        fallback: "",
      });
      const publicAddress = `${protocol}//${host}/${
        userSlug !== "None" ? userSlug : username
      }`;
      await navigator.clipboard.writeText(publicAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy the link:", err);
    }
  };

  const menuItems = [
    {
      href: "/",
      iconClass: "fas fa-home",
      label: t("navigation.home"),
      condition: true,
    },
    {
      href: "/calendarconnections",
      iconClass: "fas fa-calendar",
      label: t("navigation.calendars"),
      condition: true,
    },
    {
      href: "/leads",
      iconClass: "fa-solid fa-address-book",
      label: t("navigation.leads"),
      condition: true,
    },
    {
      href: "/integrations",
      iconClass: "fas fa-plug",
      label: t("navigation.integrations"),
      condition: true,
    },
    {
      href: "/team",
      iconClass: "fas fa-users",
      label: t("navigation.team"),
      condition: true,
    },
    {
      href: "/agency",
      iconClass: "fa-solid fa-user-tie",
      label: t("navigation.agency"),
      condition: userAgency,
    },
    {
      href: "/embed",
      iconClass: "fas fa-code",
      label: t("navigation.embed"),
      condition: true,
    },
    {
      href: "/upgrade",
      iconClass: "far fa-credit-card",
      label: t("navigation.upgrade"),
      condition: userPlan !== "professional",
    },
  ];

  // Only hide on login/onboarding and on mobile
  const hiddenRoute =
    window.location.pathname === "/login" ||
    window.location.pathname === "/signin" ||
    window.location.pathname === "/onboarding";

  if (hiddenRoute || isMobile || !hasSession) return null;

  return (
    <>
      <aside
        id="sidebar"
        className={`tw-fixed tw-top-0 tw-left-0 tw-h-screen tw-transition-width tw-duration-300 ${
          isSidebarExpanded ? "tw-w-64" : "tw-w-16"
        } tw-bg-white dark:tw-bg-gray-800 tw-border-r tw-border-gray-200 dark:tw-border-gray-700`}
        aria-label="Sidebar"
      >
        <div className="tw-flex tw-flex-col tw-overflow-y-auto tw-py-10 tw-px-3 tw-h-full">
          <Link
            to="/"
            className={`tw-flex tw-flex-row tw-items-center tw-justify-center`}
          >
            <Logo width="33px" fillColor={logoColor} height="30px" />
            {isSidebarExpanded && (
              <span className="tw-mb-1.5 tw-text-xl tw-font-bold">
                <Logotype fillColor={logoColor} height="25px" width="120px" />
              </span>
            )}
          </Link>
          <ul className="tw-flex-1 tw-mt-10">
            {menuItems
              .filter((item) => item.condition)
              .map((item, index) => {
                const isActive = location.pathname === item.href;
                return (
                  <li key={index}>
                    <Link
                      to={item.href}
                      className={`tw-flex tw-items-center tw-p-2 tw-rounded-lg tw-transition tw-duration-75 hover:tw-bg-gray-200 dark:hover:tw-bg-gray-700 ${
                        isActive ? "" : "tw-text-gray-400 dark:tw-text-gray-100"
                      } ${isSidebarExpanded ? "" : "tw-justify-center"}`}
                    >
                      {/* For the Upgrade item, wrap the icon in a relative container and display the discount badge if available */}
                      {item.href === "/upgrade" ? (
                        <div className="tw-relative">
                          <i
                            className={`${item.iconClass} tw-text-lg tw-w-7 tw-text-center`}
                            style={{
                              color: isActive ? logoColor : undefined,
                            }}
                          ></i>
                          {discountPercentage && (
                            <span className="tw-absolute tw--bottom-1 tw-right-3 tw-bg-primary-500 dark:tw-bg-primary-400 tw-text-white tw-rounded-full tw-text-xs tw-font-bold tw-px-1 tw-transform tw-translate-x-1/2 tw-translate-y-1/2">
                              -{discountPercentage}%
                            </span>
                          )}
                        </div>
                      ) : (
                        <i
                          className={`${item.iconClass} tw-text-lg tw-w-7 tw-text-center`}
                          style={{
                            color: isActive ? logoColor : undefined,
                          }}
                        ></i>
                      )}
                      {isSidebarExpanded && (
                        <span className="tw-ml-3 tw-text-gray-400">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
          </ul>
          <div className="tw-my-3">
            {/* Profile Obj */}
            <Link
              to={publicAddress}
              className={`tw-flex tw-my-2 tw-items-center ${
                isSidebarExpanded ? "tw-px-3" : "tw-pl-1 tw-pr-0"
              } tw-py-2 tw-rounded hover:tw-bg-gray-50 dark:hover:tw-bg-gray-600`}
            >
              <img
                src={userPic}
                className={`tw-mr-3 tw-w-8 tw-h-8 tw-rounded-full ${shadowColorClass}`}
                alt={userFullName}
              />
              {isSidebarExpanded && (
                <div>
                  <div className="tw-text-left">
                    <div className="tw-font-semibold tw-leading-none tw-text-gray-900 dark:tw-text-white tw-mb-0.5">
                      {userFullName}
                    </div>
                    <div className="tw-text-sm tw-text-primary-700 dark:tw-text-primary-400">
                      {userPlan}
                    </div>
                  </div>
                </div>
              )}
            </Link>

            {/* Copy Link */}
            <button
              onClick={handleCopy}
              className={`tw-flex tw-items-center tw-py-1.5 ${
                isSidebarExpanded ? "tw-px-5" : "tw-px-3"
              } tw-text-sm tw-text-gray-400 hover:tw-text-gray-900 dark:hover:tw-text-white`}
              type="button"
            >
              <i
                className={
                  copied
                    ? "far fa-check-circle tw-w-5 tw-h-5 tw-content-center tw-text-center"
                    : "fas fa-link tw-w-5 tw-h-5 tw-content-center tw-text-center"
                }
              ></i>
              {isSidebarExpanded && (
                <span className="tw-ml-3">{t("functions.copyLink")}</span>
              )}
            </button>

            {/* Settings */}
            <Link
              to="/settings"
              className={`tw-flex tw-items-center tw-py-1 tw-rounded-lg ${
                location.pathname === "/settings"
                  ? ""
                  : "tw-text-gray-400 hover:tw-text-gray-900 dark:hover:tw-text-white"
              } ${
                isSidebarExpanded
                  ? "tw-px-5"
                  : "tw-ml-1.5 tw-px-3 tw-justify-center"
              }`}
            >
              <i
                className={`fas fa-cog tw-text-sm tw-w-5 tw-h-5 tw-content-center tw-text-center`}
                style={{
                  color:
                    location.pathname === "/settings" ? logoColor : undefined,
                }}
              ></i>
              {isSidebarExpanded && (
                <span className="tw-ml-3 tw-text-sm">
                  {t("navigation.settings")}
                </span>
              )}
            </Link>

            {/* Theme Switcher */}
            {theme === "light" ? (
              <button
                onClick={() => {
                  setTheme("dark");
                  setDomTheme("dark");
                }}
                className={`tw-flex tw-items-center tw-py-1.5 ${
                  isSidebarExpanded ? "tw-px-5" : "tw-px-3"
                } tw-text-sm tw-text-gray-400 hover:tw-text-gray-900 dark:hover:tw-text-white`}
                type="button"
              >
                <i className="fas fa-moon tw-text-center tw-w-5 tw-h-5 tw-content-center tw-text-center"></i>
                {isSidebarExpanded && (
                  <span className="tw-ml-3 tw-text-sm">
                    {t("functions.darkTheme")}
                  </span>
                )}
              </button>
            ) : (
              <button
                onClick={() => {
                  setTheme("light");
                  setDomTheme("light");
                }}
                className={`tw-flex tw-items-center tw-py-1.5 ${
                  isSidebarExpanded ? "tw-px-5" : "tw-px-3"
                } tw-text-sm tw-text-gray-400 hover:tw-text-gray-900 dark:hover:tw-text-white`}
                type="button"
              >
                <i className="fas fa-sun tw-text-sm tw-w-5 tw-h-5 tw-content-center tw-text-center"></i>
                {isSidebarExpanded && (
                  <span className="tw-ml-3 tw-text-sm">
                    {t("functions.lightTheme")}
                  </span>
                )}
              </button>
            )}

            {/* Logout */}
            <Link
              to="/logout"
              className={`tw-flex tw-items-center tw-py-1 tw-rounded-lg ${
                location.pathname === "/logout"
                  ? ""
                  : "tw-text-gray-400 hover:tw-text-gray-900 dark:hover:tw-text-white"
              } ${
                isSidebarExpanded
                  ? "tw-px-5"
                  : "tw-ml-1.5 tw-px-3 tw-justify-center"
              }`}
            >
              <i
                className={`fas fa-sign-out-alt tw-text-sm tw-w-5 tw-h-5 tw-content-center tw-text-center`}
                style={{
                  color:
                    location.pathname === "/logout" ? logoColor : undefined,
                }}
              ></i>
              {isSidebarExpanded && (
                <span className="tw-ml-3 tw-text-sm">
                  {t("navigation.logout")}
                </span>
              )}
            </Link>
          </div>

          {/* Collapse Toggle Button */}
          <button
            className={`tw-flex tw-items-center tw-py-2 tw-rounded-lg tw-transition tw-duration-75 tw-text-gray-400 hover:tw-text-gray-900 dark:hover:tw-text-white tw-mt-auto ${
              isSidebarExpanded ? "tw-px-6" : "tw-px-3 tw-justify-center"
            }`}
            onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
          >
            <i
              className={`fas ${
                isSidebarExpanded ? "fa-angle-left" : "fa-angle-right"
              } tw-text-sm`}
            ></i>
            {isSidebarExpanded && (
              <span className="tw-ml-3 tw-text-sm">
                {t("functions.collapse")}
              </span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
